// ticket-app-fe/src/components/TicketPurchaseForm.jsx
// MERGED: Adds discount code validation + free-ticket flow to AppSync/Stripe form

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  useStripe,
  useElements,
  CardElement,
  PaymentRequestButtonElement
} from '@stripe/react-stripe-js';
import { generateClient } from 'aws-amplify/api';
import { houstonZipMappings } from '../utils/zipMappings';

const client = generateClient({ authMode: 'apiKey' });

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': { color: '#aab7c4' }
    },
    invalid: { color: '#fa755a', iconColor: '#fa755a' }
  }
};

const TicketPurchaseForm = ({
  eventCode,
  eventName,
  eventPrice,
  eventDate,
  eventTime,
  venueName,
  venueAddress,
  eventImage, // optional
  onSuccess
}) => {
  const stripe = useStripe();
  const elements = useElements();

  // -------- Form & UI State --------
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    zipCode: '',
    organization: '',
    discountCode: ''
  });

  const [community, setCommunity] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [ticketInfo, setTicketInfo] = useState(null);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const paymentRequestRef = useRef(null);
  const [walletReady, setWalletReady] = useState(false);
  const walletKindRef = useRef('CARD'); // 'APPLE_PAY' | 'GOOGLE_PAY' | 'CARD'

  // -------- Discount / Price State --------
  const [quantity, setQuantity] = useState(1);
  const [finalPrice, setFinalPrice] = useState(eventPrice || 0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountMessage, setDiscountMessage] = useState('');
  const [checkingDiscount, setCheckingDiscount] = useState(false);

  // Helper to compute per-ticket net after discount
  const unitBase = Number(eventPrice || 0);
  const unitDiscount = Number(discountAmount || 0);
  const unitNet = Math.max(0, unitBase - unitDiscount);
  const orderTotal = unitNet * quantity;

  const isZipValid = /^\d{5}$/.test(formData.zipCode || '');

const canShowWalletButton = !!stripe
  && orderTotal > 0
  && isZipValid
  && !checkingDiscount
  && !loading;

/** What we require BEFORE the wallet sheet opens */
const missingBeforeWallet = () => {
  const missing = [];
  if (!isZipValid) missing.push('ZIP code');
  // Full name/email/phone can be provided by the wallet sheet,
  // so we don't require them *before* opening.
  return missing;
};

/** What we require BEFORE a non-wallet submit */
const missingBeforeCard = () => {
  const missing = [];
  if (!formData.fullName?.trim()) missing.push('Full name');
  if (!formData.email?.trim()) missing.push('Email');
  if (!formData.phone?.trim()) missing.push('Phone');
  if (!isZipValid) missing.push('ZIP code');
  return missing;
};


  // Keep final price in sync if eventPrice prop changes or code cleared
  useEffect(() => {
    const per = Math.max(0, (eventPrice || 0) - (discountAmount || 0));
    setFinalPrice(per * quantity); // finalPrice now equals order total
  }, [eventPrice, discountAmount, quantity]);

  useEffect(() => {
    if (!formData.discountCode) {
      setDiscountAmount(0);
      setDiscountMessage('');
    }
  }, [formData.discountCode]);

  // -------- Apple Pay / Google Pay Setup (updates when finalPrice changes) --------
// Create ONCE
useEffect(() => {
  if (!stripe) return;

  // If free or basic preconditions fail, hide wallet
  if (orderTotal === 0 || !isZipValid) {
    setPaymentRequest(null);
    return;
  }

  // Create once
  if (!paymentRequest) {
    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: { label: eventName, amount: Math.round(orderTotal * 100) },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
    });

    pr.canMakePayment().then((result) => {
      console.log('Wallet canMakePayment result:', result);
      setPaymentRequest(result ? pr : null);
    });

    pr.on('paymentmethod', async (e) => {
      // Final gate: if user somehow opened sheet without ZIP, stop
      if (!isZipValid) {
        setError('Please enter a valid 5-digit ZIP code before paying.');
        e.complete('fail');
        return;
      }

      const walletName = e.walletName || '';
      const paymentType = walletName.toLowerCase().includes('apple') ? 'APPLE_PAY' : 'GOOGLE_PAY';

      // Autofill from wallet
      setFormData((prev) => ({
        ...prev,
        fullName: e.payerName || prev.fullName,
        email: e.payerEmail || prev.email,
        phone: e.payerPhone || prev.phone,
      }));

      const { purchaseError } = await handlePurchaseWithPaymentMethod(e.paymentMethod.id, paymentType);
      e.complete(purchaseError ? 'fail' : 'success');
    });

    return; // wait for setPaymentRequest
  }

  // Update the existing PR total when price changes (prevents the "mutable property" warning)
  paymentRequest.update({
    total: { label: eventName, amount: Math.round(orderTotal * 100) },
  });
}, [stripe, paymentRequest, orderTotal, eventName, isZipValid]);


// Update total when price changes (do NOT recreate PR)
useEffect(() => {
  if (paymentRequestRef.current) {
    paymentRequestRef.current.update({
      total: { label: eventName || 'Ticket', amount: Math.round((orderTotal || 0) * 100) },
    });
  }
}, [orderTotal, eventName]);


  // -------- Community from ZIP --------
  useEffect(() => {
    if (formData.zipCode.length === 5) {
      const mapping = houstonZipMappings[formData.zipCode];
      setCommunity(mapping ? mapping.community : 'Greater Houston Area');
    } else {
      setCommunity('');
    }
  }, [formData.zipCode]);

  // -------- Change Handlers --------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // -------- Discount Validation (debounced) --------
  const validateDiscountCode = useCallback(async () => {
    const code = formData.discountCode?.trim();
    if (!code) {
      setFinalPrice(eventPrice || 0);
      setDiscountAmount(0);
      setDiscountMessage('');
      return;
    }
    console.log ('Validating discount code:', code);
    setCheckingDiscount(true);
    setDiscountMessage('Checking code...');

    try {
      // First try with specific eventCode
      let response = await client.graphql({
        query: `
          query ValidateDiscount($code: String!, $eventCode: String!, $price: Float!) {
            validateDiscountCode(code: $code, eventCode: $eventCode, ticketPrice: $price) {
              valid
              discountAmount
              discountType
              message
            }
          }
        `,
        variables: {
          code,
          eventCode,
          price: eventPrice
        },
        authMode: 'apiKey'
      });

      let result = response?.data?.validateDiscountCode;
      console.log('Discount validation result:', result);
      // If not valid for specific event, try with 'ALL'
      if (!result?.valid) {
        response = await client.graphql({
          query: `
            query ValidateDiscount($code: String!, $eventCode: String!, $price: Float!) {
              validateDiscountCode(code: $code, eventCode: $eventCode, ticketPrice: $price) {
                valid
                discountAmount
                discountType
                message
              }
            }
          `,
          variables: {
            code,
            eventCode: 'ALL',
            price: eventPrice
          },
          authMode: 'apiKey'
        });
        console.log('Discount validation result (ALL):', response?.data?.validateDiscountCode);
        result = response?.data?.validateDiscountCode;
      }
      
      if (result?.valid) {
        const amt = Number(result.discountAmount || 0);
        setDiscountAmount(amt);
        setDiscountMessage(`✅ Discount applied: -$${amt.toFixed(2)} per ticket`);
      } else {
        setDiscountAmount(0);
        setDiscountMessage(`❌ ${result?.message || 'Invalid code'}`);
      }
 } catch (err) {
  console.error('Error validating discount:', err);
  setDiscountAmount(0);
  setDiscountMessage('❌ Invalid discount code');
} finally {
  setCheckingDiscount(false);
}
  }, [formData.discountCode, eventCode, eventPrice]);

useEffect(() => {
  const t = setTimeout(() => {
    if (formData.discountCode) validateDiscountCode();
    else {
      // clear if user erased code
      setDiscountAmount(0);
      setDiscountMessage('');
    }
  }, 500);
  return () => clearTimeout(t);
}, [formData.discountCode, validateDiscountCode, eventPrice]);

// -------- Submit Handlers --------
const handleSubmit = async (e) => {
  e.preventDefault();
 const missing = missingBeforeCard();
  if (missing.length) {
    setError(`Please complete: ${missing.join(', ')}`);
    return;
  }

  if (!community) {
    setError('Please enter a valid Houston ZIP code');
    return;
  }

  setLoading(true);
  setError('');

  try {
    // If free after discount → skip Stripe entirely
    if (orderTotal === 0) {
      await handleFreeTicketPurchase();
      return;
    }

    if (!stripe || !elements) {
      setError('Payments are not ready. Please try again in a moment.');
      setLoading(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);
    const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
      },
    });

    if (pmError) {
      setError(pmError.message);
      setLoading(false);
      return;
    }

    await handlePurchaseWithPaymentMethod(paymentMethod.id, 'CARD');
  } catch (err) {
    console.error('Purchase error:', err);
    setError(err.message || 'An error occurred. Please try again.');
    setLoading(false);
  }
};

const handleFreeTicketPurchase = async () => {
  try {
    const response = await client.graphql({
      query: `
          mutation PurchaseTicket($input: PurchaseTicketInputInput!) {
            purchaseTicket(input: $input) {
              success
              ticketNumber
              userId
              confirmationEmailSent
              message
              error
            }
          }
        `,
      variables: {
        input: {
          eventCode,
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          zipCode: formData.zipCode,
          organization: formData.organization || null,
          discountCode: formData.discountCode || null,
          stripePaymentMethodId: 'FREE_TICKET',
          paymentMethod: 'CARD',
          eventName,
          eventDate,
          eventTime,
          venueName,
          venueAddress,
          quantity,
          ticketPrice: orderTotal
        }
      },
      authMode: 'apiKey'
    });

    const result = response?.data?.purchaseTicket;
    if (result?.success) {
      setSuccess(true);
      setTicketInfo({
        ticketNumber: result.ticketNumber,
        userId: result.userId,
        community
      });

      setFormData({
        fullName: '',
        email: '',
        phone: '',
        zipCode: '',
        organization: '',
        discountCode: ''
      });

      setTimeout(() => onSuccess && onSuccess(), 3000);
    } else {
      setError(result?.error || 'Purchase failed');
    }
  } catch (err) {
    console.error('GraphQL error:', err);
    setError(err.errors?.[0]?.message || 'An error occurred. Please try again.');
  } finally {
    setLoading(false);
  }
};

const handlePurchaseWithPaymentMethod = async (paymentMethodId, paymentType, walletData = null) => {
  try {
    const response = await client.graphql({
      query: `
          mutation PurchaseTicket($input: PurchaseTicketInputInput!) {
            purchaseTicket(input: $input) {
              success
              ticketNumber
              userId
              confirmationEmailSent
              message
              error
            }
          }
        `,
      variables: {
        input: {
          eventCode,
          fullName: walletData?.fullName || formData.fullName,
          email: walletData?.email || formData.email,
          phone: walletData?.phone || formData.phone,
          zipCode: formData.zipCode,
          organization: formData.organization || null,
          discountCode: formData.discountCode || null,
          stripePaymentMethodId: paymentMethodId,
          paymentMethod: paymentType,
          eventName,
          eventDate,
          eventTime,
          venueName,
          venueAddress,
          quantity,
          ticketPrice: orderTotal
        }
      },
      authMode: 'apiKey'
    });

    const result = response?.data?.purchaseTicket;

    if (result?.success) {
      setSuccess(true);
      setTicketInfo({
        ticketNumber: result.ticketNumber,
        userId: result.userId,
        community
      });

      setFormData({
        fullName: '',
        email: '',
        phone: '',
        zipCode: '',
        organization: '',
        discountCode: ''
      });

      setTimeout(() => onSuccess && onSuccess(), 3000);
      return { purchaseError: null };
    } else {
      const msg = result?.error || 'Purchase failed';
      setError(msg);
      return { purchaseError: msg };
    }
  } catch (err) {
    console.error('GraphQL error:', err);
    const msg = err?.errors?.[0]?.message || 'An error occurred. Please try again.';
    setError(msg);
    return { purchaseError: msg };
  } finally {
    setLoading(false);
  }
};

// -------- Success Screen --------
if (success && ticketInfo) {
  return (
    <div className="success-container">
      <div className="success-icon">
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
          <circle cx="32" cy="32" r="32" fill="#4caf50" />
          <path d="M20 32l8 8 16-16" stroke="white" strokeWidth="4" strokeLinecap="round" />
        </svg>
      </div>
      <h3 className="success-title">You're All Set! 🎉</h3>
      <p className="success-message">Check your email for your digital ticket and event details.</p>
      <div className="success-details">
        <div className="success-detail-item">
          <span className="success-detail-label">Your ID:</span>
          <span className="success-detail-value">{ticketInfo.userId}</span>
        </div>
        <div className="success-detail-item">
          <span className="success-detail-label">Ticket #:</span>
          <span className="success-detail-value">{ticketInfo.ticketNumber}</span>
        </div>
        <div className="success-detail-item">
          <span className="success-detail-label">Community:</span>
          <span className="success-detail-value">{ticketInfo.community}</span>
        </div>
      </div>
    </div>
  );
}

// -------- Main Form --------
return (
  <form onSubmit={handleSubmit} className="ticket-form">
    {error && (
      <div className="alert alert-error">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" />
        </svg>
        {error}
      </div>
    )}

    {/* Event Header with Quantity */}
    <div className="event-header">
 <div className="price-display">
  <div className="price-row">
   
    <span className="event-name"><strong>{eventName}</strong></span>
  </div>
  <div className="price-row">
    <span><strong>Quantity:</strong></span><br></br>
    <span className="quantity-control">
      <button type="button" onClick={() => setQuantity(q => Math.max(1, q - 1))}>−</button>
      <input
        type="number"
        min="1"
        max="6"
        value={quantity}
        onChange={(e) => {
          const v = parseInt(e.target.value || '1', 10);
          setQuantity(Number.isFinite(v) ? Math.min(6, Math.max(1, v)) : 1);
        }}
        className="qty-input"
      />
      <button type="button" onClick={() => setQuantity(q => Math.min(6, q + 1))}>+</button>
    </span>
  </div>
</div>

    </div>

    {/* Price Display */}
    <div className="price-display">
      <div className="price-row">
        <span>Unit Price:</span>
        <span>${unitBase.toFixed(2)}</span>
      </div>

      {discountAmount > 0 && (
        <div className="discount-row">
          <span>Unit Discount:</span>
          <span className="discount-amount">-${unitDiscount.toFixed(2)}</span>
        </div>
      )}

      <div className="total-row">
        <span>Order Total:</span>
        <span className="total-price">
          ${orderTotal.toFixed(2)}{orderTotal === 0 && <span className="free-badge"> FREE!</span>}
        </span>
      </div>
    </div>

    {/* Your Info */}
    <div className="form-section">
      <h4 className="form-section-title">Your Information</h4>

      <div className="form-group">
        <label className="form-label">Full Name *</label>
        <input
          type="text"
          name="fullName"
          value={formData.fullName}
          onChange={handleChange}
          className="form-input"
          placeholder="John Doe"
          required
        />
      </div>

      <div className="form-group">
        <label className="form-label">Email *</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className="form-input"
          placeholder="john@example.com"
          required
        />
      </div>

      <div className="form-group">
        <label className="form-label">Phone *</label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className="form-input"
          placeholder="(555) 123-4567"
          required
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">ZIP Code *</label>
          <input
            type="text"
            name="zipCode"
            value={formData.zipCode}
            onChange={handleChange}
            className="form-input"
            placeholder="77002"
            maxLength="5"
            pattern="[0-9]{5}"
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Community</label>
          <div className="form-input-readonly">{community || '—'}</div>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Organization (Optional)</label>
        <input
          type="text"
          name="organization"
          value={formData.organization}
          onChange={handleChange}
          className="form-input"
          placeholder="Your Company"
        />
      </div>

      {/* Discount Code */}
      <div className="form-group">
        <label className="form-label">Discount Code (Optional)</label>
        <input
          type="text"
          name="discountCode"
          value={formData.discountCode}
          onChange={handleChange}
          className="form-input"
          placeholder="DISCOUNTCODE"
        />
        {checkingDiscount && (
          <div className="discount-message checking">Checking code...</div>
        )}
        {!!discountMessage && !checkingDiscount && (
          <div className={`discount-message ${discountAmount > 0 ? 'success' : 'error'}`}>
            {discountMessage}
          </div>
        )}
      </div>
    </div>

    {/* Payment Methods (hidden if free) */}
    {orderTotal > 0 && (
      <div className="form-section">
        <h4 className="form-section-title">Payment Method</h4>

        {paymentRequest && (
          <div className="payment-request-button">
            <PaymentRequestButtonElement options={{ paymentRequest }} />
            <div className="payment-divider"><span>or pay with card</span></div>
          </div>
        )}




        <div className="card-element-container">
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>

        <div className="form-disclaimer">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm.93 12.5h-1.8v-1.8h1.8v1.8zm-.08-3.48h-1.66V4.75h1.66v4.27z" />
          </svg>
          <span>Your payment is secure and encrypted</span>
        </div>
      </div>
    )}

    {/* Submit */}
    <button type="submit" className="submit-button" disabled={loading || (!stripe && orderTotal > 0)}>
      {loading ? (
        <span className="loading-spinner"></span>
      ) : orderTotal === 0 ? (
        `Get Free Ticket${quantity > 1 ? 's' : ''}`
      ) : (
        `Purchase Ticket${quantity > 1 ? 's' : ''} - $${orderTotal.toFixed(2)}`
      )}
    </button>

    <p className="form-footer">
      By purchasing, you agree to our Terms of Service and Privacy Policy.
      You'll receive a confirmation email with your digital ticket.
    </p>
  </form>
);
};

export default TicketPurchaseForm;
